import * as Sentry from '@sentry/vue'

function sentryEnabled(): boolean {
    return !!Sentry.getClient()?.getDsn()
}

/**
 * Most sessions record in "buffer" mode, keeping the replay in memory until something asks
 * for it: without the flush the feedback would point at a replay Sentry doesn't get.
 */
async function ensureReplay(): Promise<void> {
    const replay = Sentry.getReplay()

    if (!replay) {
        return
    }

    try {
        // start() throws if a replay is already in progress, hence the id check
        if (!replay.getReplayId()) {
            replay.start()
        }

        await replay.flush()
    } catch (err) {
        console.warn("Could not attach a session replay to the support request", err)
    }
}

export type SupportRequestContext = {
    userLabel?: string
    userId?: number
}

/**
 * Reports a support message to Sentry as a User Feedback item, with the session replay that
 * led to it attached. Meant for the first message of a thread only: the conversation itself
 * lives in the admin dashboard, Sentry just needs to know someone got stuck.
 */
export async function reportSupportRequest(message: string, ctx: SupportRequestContext = {}): Promise<void> {
    if (!sentryEnabled() || !message) {
        return
    }

    await ensureReplay()

    try {
        // sendFeedback forks the scope that's current when it's called
        await Sentry.withScope((scope) => {
            scope.setTag("support_request", true)
            scope.setContext("support_request", {
                user_id: ctx.userId,
                user_label: ctx.userLabel,
            })

            return Sentry.sendFeedback({
                name: ctx.userLabel,
                message,
            }, { includeReplay: true })
        })
    } catch (err) {
        // Sentry being unreachable must never block the actual support message
        console.warn("Could not report the support request to Sentry", err)
    }
}
